export const MARKER_BUBBLE_SIZE = 40

// Soft shadow for the marker's solid shapes. Applied per-shape (bubble, dot)
// rather than on a shared wrapper because a `filter` clips its subtree to the
// filter region — which cut the tooltip label off once it rose above the bubble.
const DROP_SHADOW = 'drop-shadow(0 3px 6px rgba(0,0,0,0.28))'

export type MapMarkerOptions = {
  icon: string
  label?: string
  /** Optional second tooltip line, dimmed, beneath `label` (e.g. a mine's place). */
  sublabel?: string
  important?: boolean
  /** Optional: a journey's markers are inert, and drop the pointer cursor with it. */
  onClick?: () => void
}

export type MarkerIconMount = {
  el: HTMLElement
  src: string
  alt: string
  width: number
  height: number
}

export type MapMarkerResult = {
  el: HTMLElement
  icons: MarkerIconMount[]
}

export function createMapMarker({
  icon,
  label,
  sublabel,
  important,
  onClick,
}: MapMarkerOptions): MapMarkerResult {
  // Outer wrapper — Mapbox uses this for sizing/anchor, keep it untransformed
  const el = document.createElement('div')
  el.classList.add('mine-marker')
  Object.assign(el.style, { cursor: onClick ? 'pointer' : 'default' })

  const icons: MarkerIconMount[] = []

  // Inner wrapper — carries the scale/opacity muting so Mapbox positioning
  // (on the outer element) is unaffected.
  const inner = document.createElement('div')
  inner.classList.add('marker-inner')
  Object.assign(inner.style, {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  })

  // Bubble body
  const bubble = document.createElement('div')
  bubble.classList.add('marker-bubble')
  Object.assign(bubble.style, {
    width: `${MARKER_BUBBLE_SIZE}px`,
    height: `${MARKER_BUBBLE_SIZE}px`,
    borderRadius: '100px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    filter: DROP_SHADOW,
  })

  // Lift wrapper — the bubble and its tooltip rise together on hover/active.
  // The tooltip is appended as a *sibling* of the bubble, not a descendant, so
  // the bubble's shadow filter never clips it (see DROP_SHADOW).
  const lift = document.createElement('div')
  lift.classList.add('marker-lift')
  lift.style.position = 'relative'
  lift.appendChild(bubble)

  // Circle background centered inside the bubble
  const circle = document.createElement('div')
  Object.assign(circle.style, {
    position: 'absolute',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#F3F3F3',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  })
  bubble.appendChild(circle)

  const img = document.createElement('div')
  Object.assign(img.style, {
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  })

  bubble.appendChild(img)

  icons.push({ el: img, src: icon, alt: '', width: 18, height: 18 })

  // Triangle pointing down
  const triangle = document.createElement('div')
  triangle.classList.add('marker-triangle')
  Object.assign(triangle.style, {
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    marginTop: '-2px',
  })
  bubble.appendChild(triangle)

  // Bell badge for "important" metals, overhanging the bubble's top-right
  // corner. As a child of the bubble it inherits the hover/active transforms.
  if (important) {
    const badge = document.createElement('div')
    Object.assign(badge.style, {
      position: 'absolute',
      top: '0',
      right: '0',
      width: '14px',
      height: '14px',
      padding: '2px',
      borderRadius: '50%',
      backgroundColor: '#EA1502',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    })

    const bell = document.createElement('div')
    Object.assign(bell.style, {
      width: '10px',
      height: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    })

    badge.appendChild(bell)

    icons.push({
      el: bell,
      src: '/mine-my-phone/icons/bell--flat.svg',
      alt: '',
      width: 10,
      height: 10,
    })
    bubble.appendChild(badge)
  }

  // Tooltip label, revealed on hover or when the marker is active — see
  // .marker-tooltip in BGMap.
  if (label) {
    const tooltip = document.createElement('div')
    tooltip.classList.add('marker-tooltip')

    const primary = document.createElement('span')
    primary.classList.add('marker-tooltip-primary')
    primary.textContent = label
    tooltip.appendChild(primary)

    // Optional second line (e.g. the mine's place), dimmed via its own class.
    if (sublabel) {
      const secondary = document.createElement('span')
      secondary.classList.add('marker-tooltip-secondary')
      secondary.textContent = sublabel
      tooltip.appendChild(secondary)
    }

    // Sibling of the bubble (not a child) — see the lift wrapper above.
    lift.appendChild(tooltip)
  }

  // Dot that appears on hover (hidden by default via CSS)
  const dot = document.createElement('div')
  dot.classList.add('marker-dot')
  // Its own shadow now that `.marker-inner` no longer carries one.
  dot.style.filter = DROP_SHADOW

  inner.appendChild(lift)
  inner.appendChild(dot)
  el.appendChild(inner)

  // Markers live inside Mapbox's canvas container, where the map's own
  // 'click' listener sits — without stopPropagation, a marker tap would
  // also count as a map-background tap (which dismisses panels on mobile).
  // Attached even without an `onClick`: swallowing the event is itself the job.
  el.addEventListener('click', (event) => {
    event.stopPropagation()
    onClick?.()
  })

  return { el, icons }
}
